
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import colors from '../../../config/colors';
import reducer from '../reducer';
import saga from '../saga';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation, generatePath, Link } from 'react-router-dom'
import routes from '../../../config/routes';

// Styles and Components
import Row from '../../../components/Layout/Row';
import Container from '../../../components/Layout/Container';
import Col4 from '../../../components/Layout/Grid/Col4';
import Col8 from '../../../components/Layout/Grid/Col8';
import H3 from '../../../components/Headings/H3';
import P from '../../../components/Paragraph';
import {NoResultsStyleSm} from '../../../components/NoResults/NoResultsStyle';
import ButtonOptionsInline from '../../../components/PageComponents/ButtonOptionsInline';
import {LoadOverlaySticky} from '../../../components/Animations';
import CustomHelmet from '../../../components/Helmet/Loadable';
import Button from '../../../components/ButtonsStyle/index';
import {Tag} from '../../../components/Tag';

// Import Actions
import { getSingleEvent, getSimiliarEvents } from '../actions';

// Import Selectors
import { makeSelectEventItem, makeSelectLoading, makeSelectSimilarEvents, makeSelectSimilarCurrentPage, makeSelectSimilarPageCount, makeSelectLoadingSimilar, makeSelectEventItemError } from '../selectors';
import { makeSelectisAuthenticated, makeSelectUserInfo } from '../../App/selectors';

// Import Messages && Date
import { FormattedMessage } from 'react-intl';
import generalMessages from '../../generalMessages';
import messages from './messages';
import { formatDateObj } from '../../../utils/date';

// Pagination
import ReactPaginate from 'react-paginate';
import PaginateWrapper from '../../../components/Pagination';

// Import ScroolToTop
import ScrollToTop from '../../ScrollToTop';

// Import Models and other configuration
import { SingleEvent } from '../../../models/eventItem';
import { ParamTypes } from '../../../models/urlParams';

// Can I - view
import Can from '../../../config/acl/can';
import {viewSections} from '../../../config/acl/aclReadSections';
import ApproveHrModal from '../../Tasks/Modal/approveHrModal';

// Share Event
import ShareEvent from '../ShareEvent';
import { STATUSES, BASE_URL, REDUX_SAGA_KEYS, CDN_URL } from '../../../config/config';

import TagManager from 'react-gtm-module';
import gtm from '../../../config/gtm'
import LinkPurple from '../../../components/LinksStyle/LinkPurple';

import EventError from './eventError';
import { truncateMetaDescription, useInjectSagaAndReducer } from '../../../utils/helpers';
import Separator from '../../../components/PageSections/Separator';
import Title from '../../../components/PageSections/Event/Title';
import MainWrapper from '../../../components/PageSections/MainWrapper';
import { Links } from '../../../components/LinksStyle';
import Details from '../../../components/PageSections/Event/Details';
import Similar from '../../../components/PageSections/Event/Similar';
import InlineButtons from '../../../components/PageSections/InlineButtons';


const EventsDetails: FC = () => {

  useInjectSagaAndReducer(REDUX_SAGA_KEYS.events, reducer, saga);
  const {id, assignedTo, taskId, taskType} = useParams<ParamTypes>();
  const location = useLocation();
  const dispatch = useDispatch();

  const apiUrl: string | undefined = BASE_URL;
  const eventStatus: string | undefined = STATUSES.expired;

  const loading = useSelector(makeSelectLoading);
  const similar = useSelector(makeSelectLoadingSimilar)
  const event : SingleEvent = useSelector(makeSelectEventItem);
  const similiarEvents = useSelector(makeSelectSimilarEvents);
  const isAuth = useSelector(makeSelectisAuthenticated);
  const userInfo = useSelector(makeSelectUserInfo);
  const eventError = useSelector(makeSelectEventItemError);


  // Pagination
  const pageCount = useSelector(makeSelectSimilarPageCount);
  const currentPage = useSelector(makeSelectSimilarCurrentPage);

  const [hrModal, setHrModal] = useState(false);
  const [category, setCategory] = useState('');
  const [shareButtonsOpen, setShareButtonsOpen] = useState(false);
  const [shareButtonsOpenBottom, setShareButtonsOpenBottom] = useState(false);

  useEffect(() => {
    if(id) {
      dispatch(getSingleEvent(id))
    }
  }, [dispatch, id]);

  useEffect(() => {
    let page = 1;
    if(category && id) {
      dispatch(getSimiliarEvents(category, id, page));
    }
  }, [dispatch, id, category]);

  const handlePageClick = useCallback((data: any) => {
    let page = data.selected >= 0 ? data.selected + 1 : 0;
    dispatch(getSimiliarEvents(category, id, page));
  }, [dispatch, id, category]);

  const aproveNews = () => {
    setHrModal(true);
  }

  useEffect(() => {
    if(event) {
      setCategory(event.category || '')
    }
  }, [event]);

  // Top Button
  const openShare = useCallback(() => {
    setShareButtonsOpen(!shareButtonsOpen);
  }, [shareButtonsOpen]);

  const openShareBottom = useCallback(() => {
    setShareButtonsOpenBottom(!shareButtonsOpenBottom);
  }, [shareButtonsOpenBottom]);

  const applyForEvent = useCallback(() => {
    if(event) {
      const tagManagerArgs = {
        dataLayer: {
            'eventCategory': event.category_name,
            'event': gtm.events.eventApply,
        },
      }
      TagManager.dataLayer(tagManagerArgs)
    }
  },[event])

  return (
    loading ?
      <LoadOverlaySticky />
    :
    event ?
    <EventDetails>
      <ScrollToTop />
      {eventError ? <EventError /> : event ?
      <>
      <CustomHelmet
        title={event.seo_title ? event.seo_title : event.title ? event.title : ''}
        pageUrl={location ? location.pathname : ''}
        description={event.seo_description ? truncateMetaDescription(event.seo_description) : event.description ? truncateMetaDescription(event.description) : ''}
        ogTitle={event.seo_title ? event.seo_title : event.title ? event.title : ''}
        ogDescription={event.seo_description ? truncateMetaDescription(event.seo_description) : event.description ? truncateMetaDescription(event.description) : ''}
        ogImage={event.seo_image_file_name && CDN_URL ? CDN_URL + event.seo_image_file_name : apiUrl ? apiUrl.substring(0, apiUrl.length - 4) + '/images/events_og.png' : ''}
        keywords={event.keywords ? event.keywords : ''}
        ogImageAlt={event.seo_image_alt ? event.seo_image_alt : null}
      />
      <Separator/>
        <Container>

        <MainWrapper>
        <Title item={event} />
          <InlineButtons>
            {event.status === STATUSES.process || event.status === STATUSES.expired ?
              null :
              <Button className={shareButtonsOpen ? 'shareShow' : ''} theme="white" onClick={openShare}>
                <svg><use xlinkHref={`#share`} /></svg>
                <FormattedMessage {...messages.ShareEvent} />
              </Button>
            }
            {event.status === STATUSES.expired ?
              <Tag theme="red_lg">
                <FormattedMessage {...messages.EventExpired} />
              </Tag> :
              <LinkPurple theme="purple" href={event.event_url} target="_blank" rel="noopener noreferrer" onClick={applyForEvent}>
                <FormattedMessage {...messages.EventSignUp} />
              </LinkPurple>
            }
            {shareButtonsOpen ?
              <ShareEvent 
                event={event}  
                openShare={shareButtonsOpen}
              />
            : null}
          </InlineButtons>
          <Row>
            <Col8Events>

            <Details item={event}/>

              {event.status !== STATUSES.process ?
                <EventPublished><strong><FormattedMessage {...messages.EventPublished} />: {formatDateObj(event.published_at)}</strong></EventPublished>
              : userInfo.unique_id === assignedTo ? null :
                <VerificationProcess><H3><FormattedMessage {...generalMessages.ProcessStatus}/></H3></VerificationProcess> }

              {userInfo.unique_id === assignedTo ?
                <Can I="view" a={viewSections.section.jobs.hrButton}>
                  <ButtonOptionsInline>
                  <Links theme='purple' to={generatePath(routes.EventAdminEdit, {'urlSlug': event.slug, 'assignedTo': assignedTo, 'id': event.unique_id,'taskId': taskId, 'taskType': taskType })}><FormattedMessage {...generalMessages.Edit} /></Links>
                  {(!event.approved && event.status !== eventStatus) ? <Button theme='primary' onClick={aproveNews}><FormattedMessage {...generalMessages.Approve} /></Button> : null}
                  </ButtonOptionsInline>
                </Can> :
              null}

            </Col8Events>
            <Col4Events>
              <SimilarEvents>
                <H3>Slični događaji</H3>
                <Link to={{pathname: routes.EventsPage, search:`category=${event.category}`}}>Pogledaj sve</Link>
                {pageCount > 1 &&
                  <PaginateWrapper>
                  <ReactPaginate
                    previousLabel={<svg><use xlinkHref={`#arrow`} /></svg>}
                    nextLabel={<svg><use xlinkHref={`#arrow`} /></svg>}
                    previousLinkClassName={"arrow-link"}
                    nextLinkClassName={"arrow-link"}
                    pageCount={pageCount}
                    forcePage={currentPage - 1}
                    marginPagesDisplayed={0}
                    pageRangeDisplayed={0}
                    onPageChange={handlePageClick}
                    containerClassName={'pagination arrows'}
                    disabledClassName={"disabled"}
                    activeClassName={'active'}
                  />
                </PaginateWrapper>
                }
              <EventList>
                {similar ?
                  <LoadOverlaySticky />
                  : <>
                  {similiarEvents && similiarEvents.length > 0 ?
                    similiarEvents.map((item: any, i: number) => {
                        return (
                          <Link to={generatePath(routes.EventsDetailPage, {'urlSlug': item.slug, 'id' : item.unique_id})} key={i}>
                            <Similar item={item} />
                          </Link>
                        )
                    })
                  : <NoResultsStyleSm><P><FormattedMessage {...messages.NoSimilarEvents} /></P></NoResultsStyleSm>
                  }
                  </>
                }
              </EventList>
              </SimilarEvents>
            </Col4Events>
          </Row>
          <InlineButtons>
            {event.status === STATUSES.process || event.status === STATUSES.expired ?
              null :
              <Button className={shareButtonsOpenBottom ? 'shareShow' : ''} theme="white" onClick={openShareBottom}>
                <svg><use xlinkHref={`#share`} /></svg>
                <FormattedMessage {...messages.ShareEvent} />
              </Button>
            }
            {event.status === STATUSES.expired ?
              <Tag theme="red_lg">
                <FormattedMessage {...messages.EventExpired} />
              </Tag> :
              <LinkPurple href={event.event_url} target="_blank" rel="noopener noreferrer" onClick={applyForEvent}>
               <FormattedMessage {...messages.EventSignUp} />
              </LinkPurple>
            }
             {shareButtonsOpenBottom ?
            <ShareEvent 
                event={event} 
                openShare={shareButtonsOpenBottom}
              />
            : null}
          </InlineButtons>
        </MainWrapper>
      </Container>
       </> : <></> }
      {event ?
        <ApproveHrModal
          closeModal={setHrModal}
          unique_id={event.unique_id}
          isOpen={isAuth && hrModal}
          model='event'
          taskId={taskId}
        /> : null
      }
    </EventDetails> : <></>
  )
}

const EventDetails = styled.div``;

const Col8Events = styled(Col8)`
  @media (max-width: 1025px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
`;

const Col4Events = styled(Col4)`
  @media (max-width: 1025px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
`;

const SimilarEvents = styled.div`
  position: relative;
  background: ${colors.GRAY_EXTRA_LIGHT};
  padding: 20px;
  a {
    font-family: "Gilroy-Medium"
  }
`;

const EventList = styled.div`
  position: relative;
  display: block;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
`;

const EventPublished = styled(P)`
  font-size: 16px;
  margin: 20px 0;
`;

const VerificationProcess = styled.div`
  margin-top: 20px;
`;

export default EventsDetails;
